import {
  type ContentObjectOption,
  ContentObjectOptions,
  type ContentObjectProps,
} from '@openx/types/targeting/contentObject';
import { Criteria } from '@openx/types/targeting/targetingValuesTypes';
import { noop } from '@openx/utils/noop';
import { Fragment, useCallback, useMemo } from 'react';
import type { KeyAsString } from 'type-fest';
import { getChipLabelVisibilityFlags } from '../../../utils/hooks/getChipLabelVisibilityFlags';
import { useParameterRemove } from '../../../utils/hooks/useParameterRemove';
import { useTargetingOptionDisabledState } from '../../../utils/hooks/useTargetingOptionDisabledState';
import { DimensionChipLine } from '../../shared/DimensionChip/DimensionChipLine';
import type { TargetingDropdownSectionProps } from '../../shared/ReadOnlyTargetingDropdown/TargetingDropdownSection';
import type { TargetingItemsProps } from '../../types';
import { UTILITY_ITEMS_SET } from '../constants';
import type { CommonItemPropsType } from '../types';
import { filterEmptyParams, getExcludeAnyWarning } from '../utils/utils';
import { EpisodeItem } from './components/EpisodeItem';
import { GenreItem } from './components/GenreItem';
import { LanguageItem } from './components/LanguageItem';
import { LivestreamItem } from './components/LivestreamItem';
import { SeriesItem } from './components/SeriesItem';
import { ExcludeAnyAlert } from './ExcludeAnyAlert';

interface ContentObjectItemsProps extends TargetingItemsProps<ContentObjectProps> {
  dataTest?: string;
}

export const ContentObjectFormPreviewItems = ({
  targetingParams,
  readonly,
  onChange = noop,
  hideButtons,
  onOpenDrawerClick,
  onRemove,
  onRemoveParameter,
  optionsCount,
}: ContentObjectItemsProps &
  TargetingDropdownSectionProps & {
    onRemoveParameter?: (opt: ContentObjectOption) => void;
  }) => {
  const switchState = targetingParams[ContentObjectOptions.CONTENT_INTER_DIMENSION_OPERATOR] === Criteria.ANY;

  const { isOptionDisabled, getOptionDisabledReason } = useTargetingOptionDisabledState();

  const dimensionChipLine = useCallback(
    (showLabel: boolean) => (
      <DimensionChipLine switchState={switchState} isSubsection={switchState} showLabel={showLabel} />
    ),
    [switchState]
  );

  const shouldDisplayAlert = useMemo(
    () => !readonly && getExcludeAnyWarning(targetingParams),
    [readonly, targetingParams]
  );

  const selectedParams = useMemo(() => new Set(Object.keys(filterEmptyParams(targetingParams))), [targetingParams]);

  const parameterRemoveHandler = useParameterRemove<ContentObjectOption>(onRemoveParameter, onRemove);

  const labelVisibilityFlags = useMemo(
    () => getChipLabelVisibilityFlags(Object.keys(targetingParams), key => selectedParams.has(key)),
    [targetingParams, selectedParams]
  );

  const items = useMemo(
    () =>
      (Object.keys(targetingParams) as KeyAsString<typeof targetingParams>[])
        .filter(type => !UTILITY_ITEMS_SET.has(type))
        .map((type, index, currentArray) => {
          const showLabel = labelVisibilityFlags[index] ?? false;
          const contentObjectOption = type as ContentObjectOption;

          const commonProps: CommonItemPropsType = {
            type: contentObjectOption,
            showDimensionChipLine: index < currentArray.length - 1,
            hideButtons,
            selectedParams: Array.from(selectedParams),
            onChange,
            onRemove: parameterRemoveHandler(contentObjectOption),
            onOpenDrawerClick: () => onOpenDrawerClick?.(contentObjectOption),
            readonly,
            dimensionChipLine: () => dimensionChipLine(showLabel),
            targetingParams,
            optionsCount,
            isDisabled: isOptionDisabled(contentObjectOption),
            disabledReasonMessage: getOptionDisabledReason(contentObjectOption),
          };

          switch (type) {
            case ContentObjectOptions.LANGUAGE:
              return (
                <Fragment key={type}>
                  <LanguageItem {...commonProps} />
                </Fragment>
              );

            case ContentObjectOptions.LIVESTREAM:
              return (
                <Fragment key={type}>
                  <LivestreamItem {...commonProps} />
                </Fragment>
              );

            case ContentObjectOptions.CONTENT_RATING:
            case ContentObjectOptions.GENRE:
              return (
                <Fragment key={type}>
                  <GenreItem {...commonProps} />
                </Fragment>
              );

            case ContentObjectOptions.EPISODE:
              return (
                <Fragment key={type}>
                  <EpisodeItem {...commonProps} />
                </Fragment>
              );

            case ContentObjectOptions.NETWORK:
            case ContentObjectOptions.CHANNEL:
            case ContentObjectOptions.SERIES:
              return (
                <Fragment key={type}>
                  <SeriesItem {...commonProps} />
                </Fragment>
              );

            default:
              return null;
          }
        })
        .filter(Boolean),
    [
      targetingParams,
      readonly,
      optionsCount,
      onChange,
      dimensionChipLine,
      hideButtons,
      onOpenDrawerClick,
      selectedParams,
      labelVisibilityFlags,
      parameterRemoveHandler,
      isOptionDisabled,
      getOptionDisabledReason,
    ]
  );

  return (
    <>
      {shouldDisplayAlert && <ExcludeAnyAlert />}
      {items}
    </>
  );
};
